/**
 * Support ticket detail — `/app/support/:id`. Read-only view of one ticket from
 * GET /support/tickets/:id (EMS 404s if the ticket isn't owned by the current
 * org). Available to both submit- and view-only admins. Handles loading /
 * not-found / error and the 503 "support unconfigured" state gracefully.
 */
import { ApiError } from '@fc/core'
import { Badge, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { getTicket } from '../../lib/support-api'
import {
  humanize,
  isUnconfigured,
  priorityVariant,
  statusVariant,
  SupportHeader,
  SupportUnconfigured,
} from './shared'

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-dim">{label}</div>
      <div className="text-sm text-fg">{value}</div>
    </div>
  )
}

export function SupportDetail() {
  const { id = '' } = useParams()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => getTicket(id),
    enabled: Boolean(id),
    retry: false,
  })

  if (isUnconfigured(error)) {
    return (
      <div className="space-y-6">
        <SupportHeader />
        <SupportUnconfigured />
      </div>
    )
  }

  const notFound = error instanceof ApiError && error.status === 404

  return (
    <div className="space-y-6">
      <Link
        to="/app/support"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" />
        Back to tickets
      </Link>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading ticket…
        </div>
      ) : notFound ? (
        <Card>
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-muted">This ticket doesn’t exist or isn’t available.</p>
            <Link to="/app/support" className="text-sm text-secondary hover:underline">
              Back to support
            </Link>
          </div>
        </Card>
      ) : isError || !data ? (
        <Card>
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load this ticket.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'The support service may be unavailable.'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            <h1 className="font-display text-2xl font-bold tracking-tight">{data.title}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(data.status)}>{humanize(data.status)}</Badge>
              <Badge variant={priorityVariant(data.priority)}>{humanize(data.priority)}</Badge>
            </div>
          </div>

          <Card className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Ticket ID" value={<code className="text-xs">{data.ticket_id}</code>} />
              <Field label="Submitter" value={data.submitter_email} />
              {data.created_at && (
                <Field label="Created" value={new Date(data.created_at).toLocaleString()} />
              )}
              {data.updated_at && (
                <Field label="Updated" value={new Date(data.updated_at).toLocaleString()} />
              )}
            </div>

            {data.description != null && String(data.description).length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-dim">
                  Description
                </div>
                <p className="whitespace-pre-wrap text-sm text-fg">{String(data.description)}</p>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  )
}
